import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";
import { format } from "date-fns";
import {
  FiTrendingUp,
  FiTrendingDown,
  FiDollarSign,
  FiCreditCard,
  FiClock,
  FiPackage,
  FiArrowRight,
  FiCheckCircle,
  FiXCircle,
} from "react-icons/fi";

function Dashboard() {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await api.get("/dashboard");
      setDashboard(response.data);
    } catch (err) {
      setError("Failed to load dashboard");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = "usd") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-error">{error}</div>;
  }

  const totalSpent =
    dashboard?.spendingSummary?.reduce(
      (sum, s) => sum + parseInt(s.total_spent),
      0
    ) || 0;

  const hasTokenPlans = dashboard?.tokenBalances?.length > 0;
  const totalTokensAvail = dashboard?.tokenBalances?.reduce(
    (sum, tb) => sum + parseInt(tb.total_allowance || 0), 0
  ) || 0;
  const totalTokensUsed = dashboard?.tokenBalances?.reduce(
    (sum, tb) => sum + Math.max(0, parseInt(tb.total_allowance || 0) - parseInt(tb.current_balance || 0)), 0
  ) || 0;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">
          Welcome back, {user?.firstName || "User"}!
        </h1>
        <p className="page-subtitle">
          Here's what's happening with your payments
        </p>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Active Subscriptions</div>
          <div className="stat-value">
            {dashboard?.subscriptions?.length || 0}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Spent</div>
          <div className="stat-value">{formatCurrency(totalSpent)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Unpaid Invoices</div>
          <div className="stat-value">
            {dashboard?.unpaidInvoices?.length || 0}
          </div>
        </div>
        {hasTokenPlans ? (
          <div className="stat-card">
            <div className="stat-label">Tokens Used</div>
            <div className="stat-value">
              {totalTokensUsed.toLocaleString()}
              <span style={{ fontSize: "14px", color: "var(--text-secondary)", fontWeight: 400 }}>
                {" "}/ {totalTokensAvail.toLocaleString()}
              </span>
            </div>
          </div>
        ) : (
          <div className="stat-card">
            <div className="stat-label">Active Trials</div>
            <div className="stat-value">
              {dashboard?.activeTrials?.length || 0}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-2">
        {/* Active Subscriptions */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Active Subscriptions</h2>
            <Link to="/subscriptions" className="btn btn-outline btn-sm">
              View All <FiArrowRight />
            </Link>
          </div>

          {dashboard?.subscriptions?.length > 0 ? (
            dashboard.subscriptions.slice(0, 3).map((sub) => (
              <div
                key={sub.id}
                className="subscription-card"
                style={{ marginBottom: "12px", padding: "16px" }}
              >
                <div className="flex-between">
                  <div>
                    <div className="subscription-name">
                      {sub.product_name}
                      {sub.status === "granted" && (
                        <span
                          style={{
                            marginLeft: "8px",
                            fontSize: "11px",
                            padding: "2px 8px",
                            borderRadius: "99px",
                            background: "rgba(99,102,241,0.15)",
                            color: "#818cf8",
                            fontWeight: 600,
                            textTransform: "uppercase",
                          }}
                        >
                          Granted
                        </span>
                      )}
                    </div>
                    <div className="text-muted" style={{ fontSize: "14px" }}>
                      {sub.status === "granted"
                        ? "Access granted \u2014 no expiry"
                        : sub.current_period_end
                        ? `Renews ${format(new Date(sub.current_period_end), "MMM d, yyyy")}`
                        : "No renewal date"}
                    </div>
                  </div>
                  {sub.status !== "granted" && (
                    <div className="subscription-price">
                      {formatCurrency(sub.amount, sub.currency)}
                      <span>/{sub.billing_period}</span>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <FiPackage className="empty-state-icon" />
              <p className="text-muted">No active subscriptions</p>
            </div>
          )}
        </div>

        {/* Recent Transactions */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Recent Transactions</h2>
            <Link to="/transactions" className="btn btn-outline btn-sm">
              View All <FiArrowRight />
            </Link>
          </div>

          {dashboard?.recentTransactions?.length > 0 ? (
            dashboard.recentTransactions.map((tx) => (
              <div key={tx.id} className="transaction-row">
                <div
                  className={`transaction-icon ${
                    tx.status === "succeeded" ? "success" : "failed"
                  }`}
                >
                  {tx.status === "succeeded" ? (
                    <FiCheckCircle />
                  ) : (
                    <FiXCircle />
                  )}
                </div>
                <div className="transaction-info">
                  <div className="transaction-name">
                    {tx.product_name || "Payment"}
                  </div>
                  <div className="transaction-date">
                    {format(new Date(tx.created_at), "MMM d, yyyy HH:mm")}
                  </div>
                </div>
                <div
                  className={`transaction-amount ${
                    tx.type === "refund" ? "negative" : ""
                  }`}
                >
                  {tx.type === "refund" ? "-" : ""}
                  {formatCurrency(tx.amount, tx.currency)}
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <FiCreditCard className="empty-state-icon" />
              <p className="text-muted">No recent transactions</p>
            </div>
          )}
        </div>
      </div>

      {/* Unpaid Invoices */}
      {dashboard?.unpaidInvoices?.length > 0 && (
        <div className="card mt-4">
          <div className="card-header">
            <h2 className="card-title">⚠️ Unpaid Invoices</h2>
          </div>

          <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Amount Due</th>
                <th>Due Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {dashboard.unpaidInvoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td>#{invoice.id.slice(0, 8)}</td>
                  <td>
                    {formatCurrency(invoice.amount_due, invoice.currency)}
                  </td>
                  <td>
                    {invoice.due_date
                      ? format(new Date(invoice.due_date), "MMM d, yyyy")
                      : "N/A"}
                  </td>
                  <td>
                    {invoice.invoice_url && (
                      <a
                        href={invoice.invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary btn-sm"
                      >
                        Pay Now
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Active Trials */}
      {dashboard?.activeTrials?.length > 0 && (
        <div className="card mt-4">
          <div className="card-header">
            <h2 className="card-title">🎁 Active Trials</h2>
          </div>

          {dashboard.activeTrials.map((trial) => {
            const daysLeft = Math.ceil(
              (new Date(trial.trial_end) - new Date()) / (1000 * 60 * 60 * 24)
            );
            return (
              <div
                key={trial.id}
                className="alert alert-info"
                style={{ marginBottom: "12px" }}
              >
                <FiClock style={{ marginRight: "8px" }} />
                <strong>{trial.application_id}</strong> trial - {daysLeft} days
                remaining
              </div>
            );
          })}
        </div>
      )}

      {/* Token Balances */}
      {dashboard?.tokenBalances?.length > 0 && (
        <div className="card mt-4">
          <div className="card-header">
            <h2 className="card-title">⚡ Token Usage</h2>
          </div>
          {dashboard.tokenBalances.map((tb) => {
            const used = Math.max(0, parseInt(tb.total_allowance || 0) - parseInt(tb.current_balance || 0));
            const pct = tb.total_allowance > 0 ? Math.min(100, (used / tb.total_allowance) * 100) : 0;
            const isLow = pct >= 80;
            return (
              <div key={tb.id} style={{ marginBottom: "16px" }}>
                <div className="flex-between" style={{ marginBottom: "6px" }}>
                  <span style={{ fontWeight: 500 }}>{tb.product_name}</span>
                  <span style={{ fontSize: "13px", color: isLow ? "var(--error)" : "var(--text-secondary)" }}>
                    {used.toLocaleString()} / {parseInt(tb.total_allowance || 0).toLocaleString()} tokens used
                  </span>
                </div>
                <div
                  style={{
                    background: "var(--bg-tertiary)",
                    borderRadius: "99px",
                    height: "8px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${pct}%`,
                      height: "100%",
                      borderRadius: "99px",
                      background: isLow ? "var(--error)" : "var(--primary)",
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
                {tb.next_reset_at && (
                  <div style={{ fontSize: "12px", color: "var(--text-secondary)", marginTop: "4px" }}>
                    Resets {format(new Date(tb.next_reset_at), "MMM d, yyyy")}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Dashboard;
